import { FC, ReactNode } from "react";
import { useLocalSearchParams } from "expo-router";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { Control, useForm } from "react-hook-form";

import {
  calculateWorkRequirementHours,
  getWorkRequirementLookbackMonths,
  hasWorkRequirementMonthEnded,
} from "@libs/work-requirements";
import { CoverageEventsFlowStepWrapper } from "@/blocks/CoverageEventsFlowStepWrapper";
import { VnBox } from "@/components/VnBox";
import { VnCard } from "@/components/VnCard";
import { VnForm } from "@/components/VnForm";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnText } from "@/components/VnText/VnText";
import { VnTextInput } from "@/components/VnTextInput/VnTextInput";
import {
  toScreeningMembers,
  workRequirementHoursFormSchema,
  WorkRequirementHoursFormType,
  WorkRequirementMemberDetailType,
  WorkRequirementType,
} from "@/domain/workRequirements";
import { useWorkRequirementScreeningStep } from "@/hooks/useWorkRequirementScreeningStep";
import { I18nKeyType } from "@/i18n";
import { dateAsDayjs } from "@/utils/date";
import {
  convertStringToNumber,
  currencyMask,
  getMaskedCurrency,
} from "@/utils/string";
import { cn } from "@/utils/tailwind";

const toHoursFieldValue = (value: number | null) =>
  value === null ? "" : String(value);

const toHours = (value: string | undefined) => {
  const hours = convertStringToNumber(value);
  return hours === null ? null : Math.round(hours * 100) / 100;
};

const toMonthlyIncome = (value: string | undefined) => {
  const income = convertStringToNumber(value);
  return income === null ? null : Math.round(income);
};

const toHoursAnswers = (values: WorkRequirementHoursFormType) => ({
  employmentHours: toHours(values.employmentHours),
  employmentMonthlyIncome: toMonthlyIncome(values.employmentMonthlyIncome),
  jobTrainingHours: toHours(values.jobTrainingHours),
  schoolHours: toHours(values.schoolHours),
  communityServiceHours: toHours(values.communityServiceHours),
});

type HoursSectionPropsType = {
  titleI18nKey: I18nKeyType;
  hasDivider?: boolean;
  children: ReactNode;
};

const HoursSection: FC<HoursSectionPropsType> = ({
  titleI18nKey,
  hasDivider = false,
  children,
}) => (
  <VnBox
    className={cn("gap-3 pb-5", hasDivider && "border-border border-t pt-5")}>
    <VnText variant="lgMedium" i18nKey={titleI18nKey} />
    {children}
  </VnBox>
);

type HoursInputPropsType = {
  control: Control<WorkRequirementHoursFormType>;
  name: keyof WorkRequirementHoursFormType;
  unitI18nKey: I18nKeyType;
  isCurrency?: boolean;
};

// The unit sits beside the field rather than as a label above it.
const HoursInput: FC<HoursInputPropsType> = ({
  control,
  name,
  unitI18nKey,
  isCurrency = false,
}) => (
  <VnBox className="flex-row items-center gap-3">
    <VnBox className="flex-1">
      <VnTextInput
        name={name}
        control={control}
        isRequired={false}
        placeholder="0"
        keyboardType={isCurrency ? "number-pad" : "decimal-pad"}
        mask={isCurrency ? { maskGenerator: currencyMask } : undefined}
      />
    </VnBox>
    <VnText
      className="text-muted-foreground"
      variant="lg"
      i18nKey={unitI18nKey}
    />
  </VnBox>
);

type HoursFormPropsType = {
  workRequirement: WorkRequirementType;
  member: WorkRequirementMemberDetailType;
};

const HoursForm: FC<HoursFormPropsType> = ({ workRequirement, member }) => {
  const { isSaving, saveAndContinue, goBack } =
    useWorkRequirementScreeningStep();
  const { rule } = workRequirement;

  const form = useForm<WorkRequirementHoursFormType>({
    defaultValues: {
      employmentHours: toHoursFieldValue(member.employmentHours),
      employmentMonthlyIncome:
        member.employmentMonthlyIncome === null
          ? ""
          : getMaskedCurrency(String(member.employmentMonthlyIncome)),
      jobTrainingHours: toHoursFieldValue(member.jobTrainingHours),
      schoolHours: toHoursFieldValue(member.schoolHours),
      communityServiceHours: toHoursFieldValue(member.communityServiceHours),
    },
    resolver: standardSchemaResolver(workRequirementHoursFormSchema),
    mode: "onChange",
  });

  const totalHours = calculateWorkRequirementHours({
    rule,
    hours: toHoursAnswers(form.watch()),
  });

  // Hours are always reported for the first lookback month: expected while it
  // is still ahead or in progress, completed once it has ended.
  const [firstMonth] = getWorkRequirementLookbackMonths({
    workRequirementDate: workRequirement.workRequirementDate,
    lookbackMonths: rule.lookbackMonths,
  });
  const today = dateAsDayjs().format("YYYY-MM-DD");
  const descriptionI18nKey =
    firstMonth &&
    hasWorkRequirementMonthEnded({ coverageMonth: firstMonth, today })
      ? "native:workRequirements.hours.description_completed"
      : "native:workRequirements.hours.description_expected";

  const handleNext = form.handleSubmit(values =>
    saveAndContinue({
      lastStep: "HOURS",
      householdMemberId: member.householdMemberId,
      members: toScreeningMembers(workRequirement).map(screeningMember =>
        screeningMember.householdMemberId === member.householdMemberId
          ? { ...screeningMember, ...toHoursAnswers(values) }
          : screeningMember,
      ),
    }),
  );

  return (
    <CoverageEventsFlowStepWrapper
      onBack={goBack}
      onNext={() => {
        void handleNext();
      }}
      isNextButtonLoading={isSaving}
      isNextButtonDisabled={!form.formState.isValid}>
      <VnText
        variant="headerTwo"
        i18nKey="native:workRequirements.hours.title"
        i18nOptions={{ name: member.firstName }}
      />
      <VnText
        variant="lg"
        className="text-muted-foreground mt-2"
        i18nKey={descriptionI18nKey}
        i18nOptions={{
          name: member.firstName,
          month: firstMonth ? dateAsDayjs(firstMonth).format("MMMM YYYY") : "",
        }}
      />

      <VnCard className="mt-4 gap-0 p-5">
        <VnForm form={form} isInputOnlyForm className="gap-0">
          <HoursSection titleI18nKey="native:workRequirements.hours.employment">
            <HoursInput
              control={form.control}
              name="employmentHours"
              unitI18nKey="native:workRequirements.hours.hoursPerMonth"
            />
            <HoursInput
              control={form.control}
              name="employmentMonthlyIncome"
              unitI18nKey="native:workRequirements.hours.payPerMonth"
              isCurrency
            />
            <VnText
              variant="md"
              className="text-muted-foreground"
              i18nKey="native:workRequirements.hours.employmentHint"
            />
          </HoursSection>
          <HoursSection
            titleI18nKey="native:workRequirements.hours.jobTraining"
            hasDivider>
            <HoursInput
              control={form.control}
              name="jobTrainingHours"
              unitI18nKey="native:workRequirements.hours.hoursPerMonth"
            />
          </HoursSection>
          <HoursSection
            titleI18nKey="native:workRequirements.hours.school"
            hasDivider>
            <HoursInput
              control={form.control}
              name="schoolHours"
              unitI18nKey="native:workRequirements.hours.hoursPerMonth"
            />
          </HoursSection>
          <HoursSection
            titleI18nKey="native:workRequirements.hours.communityService"
            hasDivider>
            <HoursInput
              control={form.control}
              name="communityServiceHours"
              unitI18nKey="native:workRequirements.hours.hoursPerMonth"
            />
          </HoursSection>
        </VnForm>

        <VnBox className="border-border flex-row items-center justify-between border-t pt-5">
          <VnText
            variant="lgMedium"
            i18nKey="native:workRequirements.hours.totalHours"
          />
          <VnText
            variant="lg"
            className="text-muted-foreground"
            i18nKey="native:workRequirements.hours.totalOf"
            i18nOptions={{
              total: totalHours,
              required: rule.requiredMonthlyHours,
            }}
            i18nProps={{
              components: {
                bold: (
                  <VnText
                    variant="lg"
                    className="text-foreground font-primary-semibold"
                  />
                ),
              },
            }}
          />
        </VnBox>
      </VnCard>
    </CoverageEventsFlowStepWrapper>
  );
};

export const WorkRequirementHoursScreen = () => {
  const { householdMemberId } = useLocalSearchParams<{
    householdMemberId: string;
  }>();
  const { workRequirement, isLoading, goBack } =
    useWorkRequirementScreeningStep();

  const member = workRequirement?.members.find(
    screenedMember => screenedMember.householdMemberId === householdMemberId,
  );

  if (isLoading || !workRequirement || !member) {
    return (
      <CoverageEventsFlowStepWrapper
        onBack={goBack}
        shouldShowNextButton={false}>
        <VnLoadingSpinner />
      </CoverageEventsFlowStepWrapper>
    );
  }

  // Keyed by member so moving to the next member starts a fresh form.
  return (
    <HoursForm
      key={member.householdMemberId}
      workRequirement={workRequirement}
      member={member}
    />
  );
};
